"use client";

import { memo } from "react";

// render-target: error-boundary — stylesheet may not load if root layout crashes
/**
 * Global error boundary for the entire Next.js App Router.
 *
 * This catches rendering errors that escape all nested error.tsx boundaries.
 * Must render its own <html>/<body> since it replaces the root layout when
 * triggered. Error reporting is disabled (Sentry removed 2026-08-25).
 *
 * Inline styles with CSS variable fallbacks are intentional — Tailwind CSS
 * may not be loaded when this boundary renders (root layout may have crashed).
 */


const RootGlobalError = memo(function RootGlobalError(
  { reset }: { error: Error & { digest?: string }; reset: () => void }
) {
  return (
    <html lang="en" data-component="RootGlobalError" data-location="src/app/RootGlobalError.tsx">
      <body
        style={{
          display: "flex",
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
          backgroundColor: "var(--background, #fafafa)",
          color: "var(--foreground, #18181b)",
          padding: "2rem",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: "28rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.75rem" }}>Something went wrong</h2>
          <p style={{ color: "var(--muted-foreground, #71717a)", marginBottom: "1.5rem" }}>An unexpected error occurred. Our team has been notified.</p>
          {/* Raw <button> on purpose: this boundary renders when the app itself
              failed, so it must not depend on the design system loading — which
              is also why every style here is inline. */}
          <button
            onClick={reset}
            style={{
              padding: "0.625rem 1.25rem",
              backgroundColor: "var(--primary, #18181b)",
              color: "var(--primary-foreground, #fafafa)",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
});

export default RootGlobalError;
